/**
 * UI-8b Task 3 — usePlans hook.
 *
 * Wraps `useAdminResource<Plan>('/platform/plans', { responseKey: 'plans' })`
 * for the list and base CRUD verbs. The backend returns `{ plans: Plan[] }`
 * with each plan augmented by a `tenantCount` aggregate (`COUNT(t.id)` from
 * the `plans LEFT JOIN tenants` query in apps/api/src/routes/platform/plans/list.ts).
 *
 * Permission: all underlying routes are gated by `PLATFORM_TENANTS_MANAGE` on
 * the backend; the hook imposes no client-side ACL — pages render a 403 / nav
 * guard upstream via `<RequirePermission>`.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { useAdminResource } from './useAdminApi.js'

export interface Plan {
  id: string
  name: string
  maxAgents: number
  maxConcurrentCalls: number
  features: Array<{ id: string; name: string }>
  tenantCount: number
}

export interface CreatePlanBody {
  name: string
  maxAgents: number
  maxConcurrentCalls: number
  features?: Record<string, unknown>
}

export type UpdatePlanBody = Partial<{
  name: string
  maxAgents: number
  maxConcurrentCalls: number
  features: Record<string, unknown>
}>

export interface UsePlansFilters {
  search?: string
  name?: string
  maxAgentsMin?: number
  maxAgentsMax?: number
  maxConcurrentCallsMin?: number
  maxConcurrentCallsMax?: number
  tenantCountMin?: number
  tenantCountMax?: number
}

export interface UsePlansResult {
  items: Plan[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  create: (body: CreatePlanBody) => Promise<Plan>
  update: (id: string, body: UpdatePlanBody) => Promise<Plan>
  remove: (id: string) => Promise<void>
}

export interface PlanFeature {
  id: string
  name: string
  description: string | null
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface CreatePlanFeatureBody {
  name: string
  description?: string
  enabled?: boolean
}

export type UpdatePlanFeatureBody = Partial<{
  name: string
  description: string
  enabled: boolean
}>

export interface UsePlanFeaturesResult {
  items: PlanFeature[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  create: (body: CreatePlanFeatureBody) => Promise<PlanFeature>
  update: (id: string, body: UpdatePlanFeatureBody) => Promise<PlanFeature>
  remove: (id: string) => Promise<void>
}

const BASE_PATH = '/platform/plans'

export function usePlans(filters?: UsePlansFilters): UsePlansResult {
  const base = useAdminResource<Plan>(BASE_PATH, {
    responseKey: 'plans',
    ...(filters ? { query: filters as Record<string, string | undefined> } : {}),
  })

  // useAdminResource.refetch() is fire-and-forget (bumps a fetchKey); wrap so
  // callers can `await` after a mutation. List state updates via the effect.
  const refetch = useCallback(async (): Promise<void> => {
    base.refetch()
  }, [base])

  const create = useCallback(
    async (body: CreatePlanBody): Promise<Plan> => {
      const created = await api.post<Plan>(BASE_PATH, body)
      base.refetch()
      return created
    },
    [base],
  )

  const update = useCallback(
    async (id: string, body: UpdatePlanBody): Promise<Plan> => {
      const updated = await api.patch<Plan>(`${BASE_PATH}/${id}`, body)
      base.refetch()
      return updated
    },
    [base],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`${BASE_PATH}/${id}`)
      base.refetch()
    },
    [base],
  )

  return {
    items: base.items,
    loading: base.loading,
    error: base.error,
    refetch,
    create,
    update,
    remove,
  }
}

export function usePlanLookup(): UsePlansResult {
  const [items, setItems] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.get<{ plans: Plan[] }>('/platform/plans/lookup')
      .then((res) => {
        if (!cancelled) setItems(res.plans ?? [])
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error fetching plans')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [fetchKey])

  const refetch = useCallback(async () => {
    setFetchKey((k: number) => k + 1)
  }, [])

  return {
    items,
    loading,
    error,
    refetch,
    create: async () => { throw new Error('Not supported via lookup') },
    update: async () => { throw new Error('Not supported via lookup') },
    remove: async () => { throw new Error('Not supported via lookup') },
  }
}

const FEATURES_BASE_PATH = '/platform/plans/features'

export function usePlanFeatures(): UsePlanFeaturesResult {
  const base = useAdminResource<PlanFeature>(FEATURES_BASE_PATH, {
    responseKey: 'features',
    initialLimit: 1000000,
  })

  const refetch = useCallback(async (): Promise<void> => {
    base.refetch()
  }, [base])

  const create = useCallback(
    async (body: CreatePlanFeatureBody): Promise<PlanFeature> => {
      const created = await api.post<PlanFeature>(FEATURES_BASE_PATH, body)
      base.refetch()
      return created
    },
    [base],
  )

  const update = useCallback(
    async (id: string, body: UpdatePlanFeatureBody): Promise<PlanFeature> => {
      const updated = await api.put<PlanFeature>(`${FEATURES_BASE_PATH}/${id}`, body)
      base.refetch()
      return updated
    },
    [base],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await api.delete(`${FEATURES_BASE_PATH}/${id}`)
      base.refetch()
    },
    [base],
  )

  return {
    items: base.items,
    loading: base.loading,
    error: base.error,
    refetch,
    create,
    update,
    remove,
  }
}
